import { buildMeta } from "../buildMeta";

/*
 * Sends unexpected errors somewhere a person will see them.
 *
 * With testers on the site, "it broke on my phone" is the report that arrives,
 * and neither analytics nor Speed Insights can answer it: one counts pageviews,
 * the other measures timings. Neither notices an exception. Vercel cannot
 * either -- every bug found so far returned HTTP 200, because a static site
 * serves a broken page as healthily as a working one.
 *
 * RouteErrorBoundary already catches the failures that blank the content area,
 * and until now it swallowed every one of them.
 *
 * Disabled with no DSN, which is the state this ships in: set
 * REACT_APP_SENTRY_DSN on the Vercel project to turn it on. The SDK is loaded
 * through a dynamic import so that while it is off, none of it is downloaded --
 * the main bundle took real work to get to 277 kB and should not carry a
 * reporter nobody has configured.
 */

const DSN = process.env.REACT_APP_SENTRY_DSN;

let sentry = null;
let starting = false;

/*
 * The dynamic import takes a moment, and startup errors are exactly the ones
 * worth having, so they queue here until the SDK arrives.
 */
let queue = [];
const QUEUE_LIMIT = 20;

function onWindowError(event) {
  report(event.error || new Error(event.message || "window error"));
}

function onRejection(event) {
  const reason = event.reason;

  report(reason instanceof Error ? reason : new Error(String(reason)));
}

function stopTemporaryHandlers() {
  window.removeEventListener("error", onWindowError);
  window.removeEventListener("unhandledrejection", onRejection);
}

export function report(error, context) {
  if (!DSN || !error) return;

  if (sentry) {
    sentry.captureException(error, context ? { extra: context } : undefined);
    return;
  }

  if (queue.length < QUEUE_LIMIT) queue.push([error, context]);
}

export function initErrorReporting() {
  if (!DSN || starting) return;

  starting = true;

  /*
   * Only for the window before the SDK loads -- Sentry installs its own global
   * handlers on init, so these come straight back off to avoid double reports.
   */
  window.addEventListener("error", onWindowError);
  window.addEventListener("unhandledrejection", onRejection);

  import("@sentry/react")
    .then((mod) => {
      mod.init({
        dsn: DSN,
        environment: process.env.NODE_ENV,
        /*
         * The short SHA writeBuildMeta stamps in at build time, so an error
         * names the build it came from with no second variable to set.
         */
        release: buildMeta.commit || undefined,
        /* Errors only. Speed Insights already covers performance. */
        tracesSampleRate: 0,
      });

      stopTemporaryHandlers();
      sentry = mod;

      const queued = queue;
      queue = [];
      queued.forEach(([error, context]) => report(error, context));
    })
    .catch(() => {
      /* A missing reporter is not worth breaking the page over. */
      stopTemporaryHandlers();
      queue = [];
    });
}

export default report;
